import { useState } from 'react';
import { apiFetch } from '../../lib/api.js';
import { useToast } from '../Toast.js';

interface Brand {
  primary?: string;
  secondary?: string;
  accent?: string;
  fontHeading?: string;
  fontBody?: string;
}

interface Props {
  orgId: string;
  initial: Brand | null;
  onSaved: () => void;
}

const PRESET_THEMES = [
  { name: 'Blue', primary: '#1e40af', secondary: '#1e3a5f', accent: '#f59e0b' },
  { name: 'Red', primary: '#dc2626', secondary: '#1e293b', accent: '#f59e0b' },
  { name: 'Green', primary: '#16a34a', secondary: '#1e293b', accent: '#eab308' },
  { name: 'Purple', primary: '#7c3aed', secondary: '#1e1b4b', accent: '#f59e0b' },
  { name: 'Orange', primary: '#ea580c', secondary: '#1c1917', accent: '#facc15' },
  { name: 'Teal', primary: '#0d9488', secondary: '#134e4a', accent: '#f59e0b' },
  { name: 'Black', primary: '#171717', secondary: '#262626', accent: '#ef4444' },
  { name: 'Navy', primary: '#1e3a5f', secondary: '#0f172a', accent: '#38bdf8' },
];

const FONTS = ['Inter', 'Lato', 'Montserrat', 'Open Sans', 'Roboto', 'Figtree'];

export function BrandColorsSection({ orgId, initial, onSaved }: Props) {
  const { toast } = useToast();
  const [brand, setBrand] = useState<Brand>(initial ?? { primary: '#1e40af', secondary: '#1e3a5f', accent: '#f59e0b' });
  const [isSaving, setIsSaving] = useState(false);

  const save = async () => {
    setIsSaving(true);
    try {
      await apiFetch(`/orgs/${orgId}/site-content/brand`, { method: 'PUT', body: JSON.stringify(brand) });
      toast('success', 'Brand colors saved — site rebuilding');
      onSaved();
    } catch { toast('error', 'Failed to save brand colors'); }
    finally { setIsSaving(false); }
  };

  const applyPreset = (preset: typeof PRESET_THEMES[0]) => {
    setBrand(b => ({ ...b, primary: preset.primary, secondary: preset.secondary, accent: preset.accent }));
  };

  return (
    <div className="space-y-4">
      {/* Presets */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-2">Quick themes</p>
        <div className="flex flex-wrap gap-2">
          {PRESET_THEMES.map(t => (
            <button key={t.name} onClick={() => applyPreset(t)}
              className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-gray-200 hover:border-gray-300 text-xs transition-colors">
              <span className="w-3 h-3 rounded-full" style={{ background: t.primary }} />
              {t.name}
            </button>
          ))}
        </div>
      </div>

      {/* Color pickers */}
      <div className="grid grid-cols-3 gap-4">
        {(['primary', 'secondary', 'accent'] as const).map(key => (
          <div key={key}>
            <label className="block text-xs font-medium text-gray-500 mb-1 capitalize">{key}</label>
            <div className="flex items-center gap-2">
              <input type="color" value={brand[key] ?? '#000000'}
                onChange={e => setBrand(b => ({ ...b, [key]: e.target.value }))}
                className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5" />
              <input type="text" value={brand[key] ?? ''} readOnly
                className="flex-1 text-xs font-mono bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5" />
            </div>
          </div>
        ))}
      </div>

      {/* Font selectors */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Heading Font</label>
          <select value={brand.fontHeading ?? 'Inter'}
            onChange={e => setBrand(b => ({ ...b, fontHeading: e.target.value }))}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
            {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Body Font</label>
          <select value={brand.fontBody ?? 'Inter'}
            onChange={e => setBrand(b => ({ ...b, fontBody: e.target.value }))}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
            {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
      </div>

      <button onClick={save} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors">
        {isSaving ? 'Saving...' : 'Save Brand Colors'}
      </button>
    </div>
  );
}
